// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  MultiSelect,
  Badge,
  ActionIcon,
  SimpleGrid,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconMapPin,
  IconShield,
  IconEye,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useGeoFences,
  useCreateGeoFence,
  useUpdateGeoFence,
  useDeleteGeoFence,
} from '../../hooks/useGeoFencing';
import type { GeoFence } from '../../types/geofencing';

// ── Constants ───────────────────────────────────────────────────────

const FENCE_TYPES = [
  { value: 'CIRCLE', label: 'Circle' },
  { value: 'POLYGON', label: 'Polygon' },
];

const ENFORCEMENT_MODES = [
  { value: 'STRICT', label: 'Strict - Block punch' },
  { value: 'WARNING', label: 'Warning - Allow with alert' },
  { value: 'LOG_ONLY', label: 'Log Only - Silent tracking' },
];

const ALLOWED_ACTIONS = [
  { value: 'PUNCH_IN', label: 'Punch In' },
  { value: 'PUNCH_OUT', label: 'Punch Out' },
  { value: 'BREAK_START', label: 'Break Start' },
  { value: 'BREAK_END', label: 'Break End' },
];

const ENFORCEMENT_COLORS: Record<string, string> = {
  STRICT: 'red',
  WARNING: 'orange',
  LOG_ONLY: 'gray',
};

// ── Form values ─────────────────────────────────────────────────────

interface GeoFenceFormValues {
  name: string;
  description: string;
  type: string;
  latitude: number;
  longitude: number;
  radius: number;
  address: string;
  branchId: string;
  enforcementMode: string;
  allowedActions: string[];
}

function emptyFormValues(): GeoFenceFormValues {
  return {
    name: '',
    description: '',
    type: 'CIRCLE',
    latitude: 0,
    longitude: 0,
    radius: 100,
    address: '',
    branchId: '',
    enforcementMode: 'WARNING',
    allowedActions: ['PUNCH_IN', 'PUNCH_OUT'],
  };
}

// ── Component ───────────────────────────────────────────────────────

export default function GeoFenceListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingFence, setEditingFence] = useState<GeoFence | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const { data, loading } = useGeoFences({ page, perPage: 25 });
  const createMutation = useCreateGeoFence();
  const updateMutation = useUpdateGeoFence();
  const deleteMutation = useDeleteGeoFence();

  const form = useForm<GeoFenceFormValues>({
    initialValues: emptyFormValues(),
    validate: {
      name: (value) => (value.trim().length === 0 ? 'Name is required' : null),
      type: (value) => (!value ? 'Fence type is required' : null),
      latitude: (value) =>
        value < -90 || value > 90 ? 'Latitude must be between -90 and 90' : null,
      longitude: (value) =>
        value < -180 || value > 180 ? 'Longitude must be between -180 and 180' : null,
      radius: (value, values) =>
        values.type === 'CIRCLE' && value <= 0 ? 'Radius must be greater than 0' : null,
      enforcementMode: (value) => (!value ? 'Enforcement mode is required' : null),
      allowedActions: (value) =>
        value.length === 0 ? 'At least one action is required' : null,
    },
  });

  const handleOpenCreate = useCallback(() => {
    setEditingFence(null);
    form.setValues(emptyFormValues());
    setFormModalOpen(true);
  }, [form]);

  const handleOpenEdit = useCallback(
    (fence: GeoFence) => {
      setEditingFence(fence);
      form.setValues({
        name: fence.name,
        description: fence.description ?? '',
        type: fence.type,
        latitude: fence.latitude,
        longitude: fence.longitude,
        radius: fence.radius ?? 100,
        address: fence.address ?? '',
        branchId: fence.branchId ?? '',
        enforcementMode: fence.enforcementMode,
        allowedActions: fence.allowedActions,
      });
      setFormModalOpen(true);
    },
    [form],
  );

  const handleSubmitForm = useCallback(
    async (values: GeoFenceFormValues) => {
      const payload: Record<string, unknown> = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        type: values.type,
        latitude: values.latitude,
        longitude: values.longitude,
        radius: values.type === 'CIRCLE' ? values.radius : undefined,
        address: values.address.trim() || undefined,
        branchId: values.branchId || undefined,
        enforcementMode: values.enforcementMode,
        allowedActions: values.allowedActions,
      };

      if (editingFence) {
        try {
          await updateMutation.mutate({ id: editingFence.id, ...payload });
          notifications.show({
            title: 'GeoFence Updated',
            message: `New version created for "${payload.name}".`,
            color: 'green',
          });
          setFormModalOpen(false);
        } catch (error) {
          notifications.show({
            title: 'Update Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        }
      } else {
        try {
          await createMutation.mutateAsync(payload);
          notifications.show({
            title: 'GeoFence Created',
            message: `"${payload.name}" has been created.`,
            color: 'green',
          });
          setFormModalOpen(false);
        } catch (error) {
          notifications.show({
            title: 'Creation Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        }
      }
    },
    [editingFence, createMutation, updateMutation],
  );

  const handleOpenDelete = useCallback((id: string) => {
    setDeleteTargetId(id);
    setDeleteReason('');
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTargetId || !deleteReason.trim()) return;
    deleteMutation.mutate(
      { id: deleteTargetId, reason: deleteReason.trim() },
      {
        onSuccess: () => {
          notifications.show({
            title: 'GeoFence Deactivated',
            message: 'GeoFence has been soft-deleted with a new version.',
            color: 'green',
          });
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
          setDeleteReason('');
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      },
    );
  }, [deleteTargetId, deleteReason, deleteMutation]);

  const columns: Column<GeoFence>[] = [
    { key: 'name', label: 'Name', sortable: true },
    {
      key: 'type',
      label: 'Type',
      width: 110,
      render: (row) => (
        <Badge
          variant="light"
          color={row.type === 'CIRCLE' ? 'blue' : 'violet'}
          icon={<IconMapPin size={12} />}
        >
          {row.type === 'CIRCLE' ? 'Circle' : 'Polygon'}
        </Badge>
      ),
    },
    {
      key: 'address',
      label: 'Address',
      render: (row) => (
        <Text size="sm" lineClamp={1}>
          {row.address || `${row.latitude.toFixed(5)}, ${row.longitude.toFixed(5)}`}
        </Text>
      ),
    },
    {
      key: 'enforcementMode',
      label: 'Enforcement',
      width: 160,
      render: (row) => (
        <Badge
          variant="light"
          color={ENFORCEMENT_COLORS[row.enforcementMode] ?? 'gray'}
          icon={<IconShield size={12} />}
        >
          {row.enforcementMode === 'STRICT'
            ? 'Strict'
            : row.enforcementMode === 'WARNING'
              ? 'Warning'
              : 'Log Only'}
        </Badge>
      ),
    },
    {
      key: 'employeeCount',
      label: 'Employees',
      width: 100,
      render: (row) => row.employeeCount ?? 0,
    },
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) => (row.version ? `v${row.version}` : 'v1'),
    },
    {
      key: 'isActive',
      label: 'Status',
      width: 100,
      render: (row) => (
        <StatusBadge status={row.isActive === false ? 'inactive' : 'active'} />
      ),
    },
  ];

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <Stack gap="md">
      <PageHeader
        title="GEO Fencing"
        subtitle={data ? `${data.length} geofence${data.length !== 1 ? 's' : ''} configured` : undefined}
        breadcrumbs={[
          { label: 'Settings', to: '/admin/workforce/settings' },
          { label: 'GEO Fencing' },
        ]}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={handleOpenCreate}>
            New GeoFence
          </Button>
        }
      />

      {/* Summary stats */}
      {data && data.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <IconMapPin size={20} color="var(--mantine-color-blue-6)" />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total Fences
                </Text>
                <Text fw={700} fz="xl">
                  {data.length}
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <IconShield size={20} color="var(--mantine-color-red-6)" />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Strict Enforcement
                </Text>
                <Text fw={700} fz="xl">
                  {data.filter((f) => f.enforcementMode === 'STRICT').length}
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <IconMapPin size={20} color="var(--mantine-color-green-6)" />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Active Fences
                </Text>
                <Text fw={700} fz="xl">
                  {data.filter((f) => f.isActive !== false).length}
                </Text>
              </div>
            </Group>
          </Paper>
        </SimpleGrid>
      )}

      <DataTable<GeoFence>
        columns={columns}
        data={data ?? []}
        loading={loading}
        pagination={
          data
            ? {
                page,
                pageSize: 25,
                total: data.length,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/settings/geofencing/${row.id}`)}
        emptyMessage="No geofences configured. Create one to start enforcing location-based punch rules."
        actions={(row) => (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/settings/geofencing/${row.id}`);
              }}
              title="View details"
            >
              <IconEye size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEdit(row);
              }}
              title="Edit geofence"
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDelete(row.id);
              }}
              disabled={row.isActive === false}
              title="Deactivate geofence"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        )}
      />

      {/* Create / Edit Modal */}
      <Modal
        opened={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={
          editingFence
            ? `Edit GeoFence: ${editingFence.name}`
            : 'Create GeoFence'
        }
        centered
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmitForm)}>
          <Stack gap="md">
            {editingFence && (
              <Text size="xs" c="dimmed">
                Saving will create a new version (v
                {(editingFence.version ?? 1) + 1}). The previous version is
                preserved in the append-only audit log.
              </Text>
            )}

            <TextInput
              label="Fence Name"
              placeholder="e.g. Main Office Perimeter"
              required
              {...form.getInputProps('name')}
            />

            <Textarea
              label="Description"
              placeholder="Optional description of this geofence"
              minRows={2}
              {...form.getInputProps('description')}
            />

            <Group grow>
              <Select
                label="Fence Type"
                data={FENCE_TYPES}
                required
                {...form.getInputProps('type')}
              />
              <Select
                label="Enforcement Mode"
                data={ENFORCEMENT_MODES}
                required
                {...form.getInputProps('enforcementMode')}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Latitude"
                placeholder="-90 to 90"
                min={-90}
                max={90}
                decimalScale={6}
                step={0.000001}
                required
                {...form.getInputProps('latitude')}
              />
              <NumberInput
                label="Longitude"
                placeholder="-180 to 180"
                min={-180}
                max={180}
                decimalScale={6}
                step={0.000001}
                required
                {...form.getInputProps('longitude')}
              />
            </Group>

            {form.values.type === 'CIRCLE' && (
              <NumberInput
                label="Radius (meters)"
                placeholder="e.g. 100"
                min={1}
                max={50000}
                required
                {...form.getInputProps('radius')}
              />
            )}

            {form.values.type === 'POLYGON' && (
              <Text size="xs" c="dimmed">
                Polygon coordinates are managed via the API or imported from GeoJSON.
                The center point (latitude/longitude above) is used as the reference
                point for this fence.
              </Text>
            )}

            <TextInput
              label="Address"
              placeholder="e.g. 123 Main St, City, State"
              {...form.getInputProps('address')}
            />

            <TextInput
              label="Branch ID"
              placeholder="Optional - link to a branch"
              {...form.getInputProps('branchId')}
            />

            <MultiSelect
              label="Allowed Actions"
              description="Which time actions are enforced by this geofence"
              data={ALLOWED_ACTIONS}
              required
              {...form.getInputProps('allowedActions')}
            />

            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="default"
                onClick={() => setFormModalOpen(false)}
                disabled={isMutating}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isMutating}>
                {editingFence ? 'Save New Version' : 'Create GeoFence'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Deactivate GeoFence"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This will soft-delete the geofence by creating a new inactive version.
            The previous version is preserved in the append-only audit log.
            Employees will no longer be restricted by this fence.
          </Text>
          <Textarea
            label="Reason for Deactivation"
            placeholder="Why is this geofence being deactivated?"
            required
            minRows={3}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={deleteMutation.isPending}
              disabled={!deleteReason.trim()}
            >
              Deactivate GeoFence
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
